import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
    Mic,
    MessageSquare,
    Loader2,
    Sparkles,
    Headphones,
    Brain,
    Plus,
    PanelLeftClose,
    PanelLeft,
    User,
    CheckCircle2,
    Trash2
} from 'lucide-react';
import RecordTab from './RecordTab';
import RecordingsTab from './RecordingsTab';
import { CAPTURE_TEMPLATES, getCaptureTemplate } from '../constants/captureTemplates';
import {
    createCaptureSessionThread,
    loadCaptureSessions,
    loadChatThreadById,
    saveChatThreadMessages,
    updateCaptureSessionMetadata,
    archiveChatThread
} from '../services/firebase';
import ragService from '../services/ragService';
import FormattedMessageContent from './FormattedMessageContent';
import { stripCaptureCompleteMarker } from '../constants/interviewConduct';
import {
    buildInterviewConversationHistory,
    saveCaptureSessionToBrain
} from '../utils/captureSessionUtils';

function SubTabButton({ label, active, onClick, icon: Icon }) {
    return (
        <button
            type="button"
            onClick={onClick}
            className={`flex flex-1 min-w-0 items-center justify-center gap-2 rounded-lg px-3 py-2.5 text-sm font-medium transition-all ${
                active
                    ? 'bg-white text-slate-900 shadow-sm ring-1 ring-slate-200/80'
                    : 'text-slate-600 hover:text-slate-900 hover:bg-white/50'
            }`}
        >
            <Icon className="h-4 w-4 shrink-0" />
            <span className="truncate">{label}</span>
        </button>
    );
}

function SessionStatusBadge({ session }) {
    if (session.documentId || session.status === 'saved') {
        return (
            <span className="inline-flex items-center gap-1 text-[10px] font-medium uppercase tracking-wide text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded">
                <Brain className="w-3 h-3" />
                In brain
            </span>
        );
    }
    if (session.captureComplete || session.status === 'ready_to_save') {
        return (
            <span className="text-[10px] font-medium uppercase tracking-wide text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded">
                Ready
            </span>
        );
    }
    if (session.status === 'completed') {
        return (
            <span className="text-[10px] font-medium uppercase tracking-wide text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded">
                Ended
            </span>
        );
    }
    return (
        <span className="text-[10px] font-medium uppercase tracking-wide text-violet-700 bg-violet-50 px-1.5 py-0.5 rounded">
            Active
        </span>
    );
}

function SessionChatView({
    template,
    sessionMeta,
    messages,
    input,
    setInput,
    isTyping,
    busy,
    error,
    onSend,
    onSaveToBrain,
    onEnd,
    readOnly,
    captureComplete,
    bottomRef
}) {
    const handleKeyDown = (e) => {
        if (readOnly) return;
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            onSend();
        }
    };

    const saved = Boolean(sessionMeta?.documentId || sessionMeta?.status === 'saved');

    return (
        <div className="flex flex-col flex-1 min-h-0">
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 pb-3 border-b border-slate-200/80 shrink-0">
                <div className="min-w-0">
                    <p className="text-xs font-medium text-violet-700 uppercase tracking-wide">Expert session</p>
                    <h3 className="text-lg font-semibold text-slate-900 truncate">{template.title}</h3>
                    {sessionMeta?.summary && (
                        <p className="text-sm text-slate-600 mt-2 line-clamp-3">{sessionMeta.summary}</p>
                    )}
                </div>
                {!readOnly && (
                    <div className="flex flex-wrap gap-2 shrink-0">
                        <button
                            type="button"
                            onClick={onSaveToBrain}
                            disabled={busy === 'save' || saved || messages.filter((m) => m.type === 'user').length === 0}
                            className="inline-flex items-center gap-2 px-3 py-2 text-sm rounded-xl bg-violet-600 text-white hover:bg-violet-700 disabled:opacity-50"
                        >
                            {busy === 'save' ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                            ) : saved ? (
                                <CheckCircle2 className="w-4 h-4" />
                            ) : (
                                <Brain className="w-4 h-4" />
                            )}
                            {saved ? 'Saved to brain' : 'Save to knowledge base'}
                        </button>
                        <button
                            type="button"
                            onClick={onEnd}
                            disabled={busy === 'end'}
                            className="inline-flex items-center gap-2 px-3 py-2 text-sm rounded-xl border border-slate-200 text-slate-700 hover:bg-slate-50"
                        >
                            End session
                        </button>
                    </div>
                )}
            </div>
            {captureComplete && !saved && (
                <div className="mt-3 shrink-0 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-900">
                    <p className="font-medium">Capture looks complete</p>
                    <p className="mt-1 text-emerald-800/90">
                        The interviewer has enough detail for this session. Review the summary above, then save to your
                        knowledge base — or add more below if you want to continue.
                    </p>
                </div>
            )}
            {error && (
                <p className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-xl px-3 py-2 mt-3 shrink-0">
                    {error}
                </p>
            )}
            <div className="flex-1 min-h-0 overflow-y-auto py-4 space-y-4">
                {messages.map((m, i) => (
                    <div
                        key={`${m.timestamp}-${i}`}
                        className={`flex gap-3 ${m.type === 'user' ? 'flex-row-reverse' : ''}`}
                    >
                        <div
                            className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${
                                m.type === 'user' ? 'bg-slate-800' : 'bg-violet-100'
                            }`}
                        >
                            {m.type === 'user' ? (
                                <User className="w-4 h-4 text-white" />
                            ) : (
                                <Sparkles className="w-4 h-4 text-violet-600" />
                            )}
                        </div>
                        <div
                            className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
                                m.type === 'user'
                                    ? 'bg-slate-800 text-white'
                                    : 'bg-slate-100 text-slate-800 border border-slate-200/60'
                            }`}
                        >
                            {m.type === 'ai' ? (
                                <FormattedMessageContent content={stripCaptureCompleteMarker(m.content)} />
                            ) : (
                                <p className="text-sm leading-relaxed whitespace-pre-wrap">{m.content}</p>
                            )}
                        </div>
                    </div>
                ))}
                {isTyping && (
                    <div className="flex gap-3">
                        <div className="w-8 h-8 rounded-full bg-violet-100 flex items-center justify-center shrink-0">
                            <Sparkles className="w-4 h-4 text-violet-600" />
                        </div>
                        <div className="bg-slate-100 border border-slate-200/60 rounded-2xl px-4 py-2.5 text-sm text-slate-500 inline-flex items-center gap-2">
                            <Loader2 className="w-4 h-4 animate-spin" />
                            Preparing next question…
                        </div>
                    </div>
                )}
                <div ref={bottomRef} />
            </div>
            {!readOnly && (
                <div className="pt-3 border-t border-slate-200/80 flex gap-2 shrink-0">
                    <textarea
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        onKeyDown={handleKeyDown}
                        rows={2}
                        placeholder="Share what you know — the interviewer will follow up…"
                        className="flex-1 rounded-xl border border-slate-200 px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-violet-300"
                    />
                    <button
                        type="button"
                        onClick={onSend}
                        disabled={isTyping || !input.trim()}
                        className="px-4 py-2 rounded-xl bg-slate-800 text-white text-sm font-medium hover:bg-slate-900 disabled:opacity-50 self-end"
                    >
                        Send
                    </button>
                </div>
            )}
        </div>
    );
}

function ExpertSessionsPanel({ user, projects, selectedProjectId, setSelectedProjectId, onDocumentCreated }) {
    const [sessions, setSessions] = useState([]);
    const [listOpen, setListOpen] = useState(true);
    const [busy, setBusy] = useState(null);
    const [error, setError] = useState(null);
    const [activeSessionId, setActiveSessionId] = useState(null);
    const [sessionMeta, setSessionMeta] = useState(null);
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState('');
    const [isTyping, setIsTyping] = useState(false);
    const [activeTemplate, setActiveTemplate] = useState(null);
    const [readOnly, setReadOnly] = useState(false);
    const bottomRef = useRef(null);
    const projectOptions = (Array.isArray(projects) ? projects : []).filter((p) => p?.id && !p.archived);

    const refreshSessions = useCallback(async () => {
        if (!user?.uid) return;
        const res = await loadCaptureSessions(user.uid, selectedProjectId || null);
        if (res.success) setSessions(res.sessions);
    }, [user?.uid, selectedProjectId]);

    useEffect(() => {
        refreshSessions();
    }, [refreshSessions]);

    useEffect(() => {
        bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages, isTyping]);

    const openSession = async (threadId) => {
        setError(null);
        setBusy('load');
        try {
            const res = await loadChatThreadById(threadId);
            if (!res.success) {
                setError(res.error || 'Could not load session');
                return;
            }
            const template = getCaptureTemplate(res.thread?.templateId) || {
                id: res.thread?.templateId || 'unknown',
                title: res.thread?.title || 'Capture session',
                systemPrompt: 'Continue the knowledge capture interview. Ask one clear question at a time.',
                completionCriteria: 'Cover the main topics with specific, actionable detail.',
                openingMessage: ''
            };
            setActiveTemplate(template);
            setActiveSessionId(threadId);
            setSessionMeta(res.thread);
            setMessages(Array.isArray(res.messages) ? res.messages : []);
            const saved = Boolean(res.thread?.documentId || res.thread?.status === 'saved');
            setReadOnly(saved);
        } catch (e) {
            setError(e?.message || 'Load failed');
        } finally {
            setBusy(null);
        }
    };

    const startTemplate = async (template) => {
        if (!user?.uid) return;
        setError(null);
        setBusy('start');
        setReadOnly(false);
        try {
            const opening = {
                type: 'ai',
                content: template.openingMessage,
                timestamp: new Date().toISOString()
            };
            const created = await createCaptureSessionThread(user.uid, selectedProjectId || null, {
                templateId: template.id,
                title: template.title
            });
            if (!created.success || !created.id) throw new Error(created.error || 'Could not create session');
            await saveChatThreadMessages(user.uid, created.id, [opening]);
            await updateCaptureSessionMetadata(user.uid, created.id, { status: 'active' });
            setActiveTemplate(template);
            setActiveSessionId(created.id);
            setSessionMeta({ status: 'active', templateId: template.id, title: template.title });
            setMessages([opening]);
            await refreshSessions();
        } catch (e) {
            setError(e?.message || 'Could not start session');
        } finally {
            setBusy(null);
        }
    };

    const sendMessage = async () => {
        const text = input.trim();
        if (!text || !user?.uid || !activeSessionId || !activeTemplate || isTyping || readOnly) return;
        const userMessage = { type: 'user', content: text, timestamp: new Date().toISOString() };
        const nextMessages = [...messages, userMessage];
        setMessages(nextMessages);
        setInput('');
        setIsTyping(true);
        setError(null);
        try {
            await saveChatThreadMessages(user.uid, activeSessionId, nextMessages);
        } catch (e) {
            console.warn(e);
        }
        try {
            const conversationHistory = buildInterviewConversationHistory(
                activeTemplate,
                messages,
                text
            );
            const result = await ragService.generateResponse(
                text,
                user.uid,
                selectedProjectId || null,
                user.uid,
                conversationHistory,
                { interviewMode: true, templateId: activeTemplate.id }
            );
            if (!result.success) {
                setError(result.error || result.response);
                return;
            }
            const aiMessage = {
                type: 'ai',
                content: result.response || 'Please try again.',
                timestamp: new Date().toISOString()
            };
            const finalMessages = [...nextMessages, aiMessage];
            setMessages(finalMessages);
            if (result.interviewComplete) {
                await updateCaptureSessionMetadata(user.uid, activeSessionId, {
                    captureComplete: true,
                    status: 'ready_to_save'
                });
                setSessionMeta((prev) => ({ ...prev, captureComplete: true, status: 'ready_to_save' }));
            }
            await saveChatThreadMessages(user.uid, activeSessionId, finalMessages);
            await refreshSessions();
        } catch (e) {
            setError(e?.message || 'Message failed');
        } finally {
            setIsTyping(false);
        }
    };

    const saveToBrain = async () => {
        if (!user?.uid || !activeSessionId || !activeTemplate) return;
        setBusy('save');
        setError(null);
        try {
            const result = await saveCaptureSessionToBrain({
                user,
                sessionId: activeSessionId,
                messages,
                template: activeTemplate,
                projectId: selectedProjectId || null,
                onDocumentCreated
            });
            setSessionMeta((prev) => ({
                ...prev,
                summary: result.summary,
                documentId: result.documentId,
                status: 'saved'
            }));
            setReadOnly(true);
            await refreshSessions();
        } catch (e) {
            setError(e?.message || 'Could not save to knowledge base');
        } finally {
            setBusy(null);
        }
    };

    const endSession = async () => {
        if (activeSessionId && user?.uid) {
            try {
                await updateCaptureSessionMetadata(user.uid, activeSessionId, { status: 'completed' });
            } catch (e) {
                console.warn(e);
            }
        }
        setActiveSessionId(null);
        setMessages([]);
        setActiveTemplate(null);
        setSessionMeta(null);
        setReadOnly(false);
        await refreshSessions();
    };

    const newSession = () => {
        setActiveSessionId(null);
        setMessages([]);
        setActiveTemplate(null);
        setSessionMeta(null);
        setReadOnly(false);
        setError(null);
    };

    const deleteSession = async (e, sessionId) => {
        e.stopPropagation();
        if (!user?.uid || !sessionId) return;
        const label = sessions.find((s) => s.id === sessionId)?.title || 'this session';
        if (!window.confirm(`Delete "${label}"? This cannot be undone.`)) return;
        setBusy('delete');
        setError(null);
        try {
            await archiveChatThread(user.uid, sessionId);
            if (activeSessionId === sessionId) {
                newSession();
            }
            await refreshSessions();
        } catch (err) {
            setError(err?.message || 'Could not delete session');
        } finally {
            setBusy(null);
        }
    };

    return (
        <div className="space-y-4">
            <div className="rounded-2xl border border-violet-200/80 bg-violet-50/50 p-4 flex gap-3">
                <Headphones className="w-5 h-5 text-violet-600 shrink-0 mt-0.5" />
                <div className="text-sm text-slate-700">
                    <p className="font-medium text-slate-900">Voice interviews (coming soon)</p>
                    <p className="mt-1 text-slate-600">
                        Live audio will use the same templates and LLM prompts. For now, structured chat interviews
                        save to your brain with an AI-generated summary, organized by project.
                    </p>
                </div>
            </div>

            {projectOptions.length > 0 && (
                <div>
                    <label className="text-xs font-medium text-slate-600 block mb-1">Project</label>
                    <select
                        value={selectedProjectId}
                        onChange={(e) => {
                            setSelectedProjectId(e.target.value);
                            newSession();
                        }}
                        className="w-full max-w-md rounded-xl border border-slate-200 px-3 py-2 text-sm"
                    >
                        <option value="">Workspace (no project)</option>
                        {projectOptions.map((p) => (
                            <option key={p.id} value={p.id}>
                                {p.name}
                            </option>
                        ))}
                    </select>
                </div>
            )}

            <div className="bg-white/80 backdrop-blur-xl rounded-2xl border border-slate-200/50 shadow-sm flex h-[min(72vh,680px)] min-h-[420px]">
                {listOpen && (
                    <aside className="w-56 shrink-0 border-r border-slate-200/60 flex flex-col min-h-0 bg-slate-50/80 rounded-l-2xl">
                        <div className="p-3 border-b border-slate-200/60 flex items-center justify-between gap-2">
                            <span className="text-xs font-semibold text-slate-700 uppercase tracking-wide">
                                Sessions
                            </span>
                            <div className="flex items-center gap-1">
                                <button
                                    type="button"
                                    onClick={newSession}
                                    className="p-1.5 rounded-lg bg-white border border-slate-200 hover:bg-slate-100"
                                    title="New session"
                                >
                                    <Plus className="w-4 h-4" />
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setListOpen(false)}
                                    className="p-1.5 rounded-lg text-slate-500 hover:bg-slate-100"
                                    title="Hide list"
                                >
                                    <PanelLeftClose className="w-4 h-4" />
                                </button>
                            </div>
                        </div>
                        <div className="flex-1 overflow-y-auto min-h-0 p-2 space-y-1">
                            {sessions.length === 0 && (
                                <p className="text-xs text-slate-500 px-2 py-3">No sessions yet — pick a template below.</p>
                            )}
                            {sessions.map((s) => (
                                <div
                                    key={s.id}
                                    className={`group flex items-stretch gap-0.5 rounded-lg ${
                                        activeSessionId === s.id ? 'ring-1 ring-violet-200 bg-violet-50' : ''
                                    }`}
                                >
                                <button
                                    type="button"
                                    onClick={() => openSession(s.id)}
                                    className={`flex-1 min-w-0 text-left rounded-lg px-3 py-2.5 text-sm transition-colors ${
                                        activeSessionId === s.id
                                            ? 'text-violet-900'
                                            : 'hover:bg-white text-slate-800'
                                    }`}
                                >
                                    <div className="flex items-center justify-between gap-1">
                                        <span className="font-medium truncate">{s.title || 'Session'}</span>
                                        <SessionStatusBadge session={s} />
                                    </div>
                                    {s.summary ? (
                                        <p className="text-xs text-slate-500 mt-1 line-clamp-2">{s.summary}</p>
                                    ) : (
                                        <p className="text-xs text-slate-400 mt-1">
                                            {s.updatedAt?.toDate?.()
                                                ? s.updatedAt.toDate().toLocaleDateString()
                                                : 'In progress'}
                                        </p>
                                    )}
                                </button>
                                    <button
                                        type="button"
                                        onClick={(e) => deleteSession(e, s.id)}
                                        disabled={busy === 'delete'}
                                        className="shrink-0 self-center p-2 mr-1 rounded-lg text-slate-400 opacity-0 group-hover:opacity-100 hover:text-red-600 hover:bg-red-50 transition-all"
                                        title="Delete session"
                                    >
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                </div>
                            ))}
                        </div>
                    </aside>
                )}

                <div className="flex-1 flex flex-col min-h-0 min-w-0 p-4">
                    {!listOpen && (
                        <button
                            type="button"
                            onClick={() => setListOpen(true)}
                            className="self-start mb-2 p-1.5 rounded-lg text-slate-500 hover:bg-slate-100"
                            title="Show sessions"
                        >
                            <PanelLeft className="w-4 h-4" />
                        </button>
                    )}

                    {activeSessionId && activeTemplate ? (
                        <SessionChatView
                            template={activeTemplate}
                            sessionMeta={sessionMeta}
                            messages={messages}
                            input={input}
                            setInput={setInput}
                            isTyping={isTyping}
                            busy={busy}
                            error={error}
                            onSend={sendMessage}
                            onSaveToBrain={saveToBrain}
                            onEnd={endSession}
                            readOnly={readOnly}
                            captureComplete={Boolean(sessionMeta?.captureComplete)}
                            bottomRef={bottomRef}
                        />
                    ) : (
                        <div className="overflow-y-auto min-h-0 space-y-6">
                            {error && (
                                <p className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-xl px-3 py-2">
                                    {error}
                                </p>
                            )}
                            <div>
                                <h3 className="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-2">
                                    <Sparkles className="w-4 h-4 text-amber-500" />
                                    Start an interview
                                </h3>
                                <div className="grid sm:grid-cols-2 gap-3">
                                    {CAPTURE_TEMPLATES.map((t) => (
                                        <button
                                            key={t.id}
                                            type="button"
                                            disabled={busy === 'start'}
                                            onClick={() => startTemplate(t)}
                                            className="text-left rounded-xl border border-slate-200 bg-white p-4 hover:border-violet-300 hover:shadow-sm transition-all disabled:opacity-50"
                                        >
                                            <p className="font-medium text-slate-900">{t.title}</p>
                                            <p className="text-xs text-violet-700 mt-0.5">{t.cadence}</p>
                                            <p className="text-sm text-slate-600 mt-2 line-clamp-2">{t.description}</p>
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

const KnowledgeCaptureTab = ({
    user,
    projects,
    initialSubTab = 'meeting',
    recordProps,
    recordingsProps,
    onDocumentCreated
}) => {
    const [subTab, setSubTab] = useState(initialSubTab);
    const [captureProjectId, setCaptureProjectId] = useState('');

    useEffect(() => {
        setSubTab(initialSubTab);
    }, [initialSubTab]);

    return (
        <div className="space-y-6 max-w-6xl w-full">
            <div className="flex gap-1 p-1 bg-slate-100/80 rounded-xl max-w-md">
                <SubTabButton
                    label="Meeting capture"
                    icon={Mic}
                    active={subTab === 'meeting'}
                    onClick={() => setSubTab('meeting')}
                />
                <SubTabButton
                    label="Expert sessions"
                    icon={MessageSquare}
                    active={subTab === 'sessions'}
                    onClick={() => setSubTab('sessions')}
                />
            </div>

            {subTab === 'meeting' && (
                <div className="space-y-8">
                    {recordProps && <RecordTab {...recordProps} compact />}
                    {recordingsProps && (
                        <RecordingsTab {...recordingsProps} embedded />
                    )}
                </div>
            )}

            {subTab === 'sessions' && (
                <ExpertSessionsPanel
                    user={user}
                    projects={projects}
                    selectedProjectId={captureProjectId}
                    setSelectedProjectId={setCaptureProjectId}
                    onDocumentCreated={onDocumentCreated}
                />
            )}
        </div>
    );
};

export default KnowledgeCaptureTab;
